import {
  Component,
  ChangeDetectionStrategy,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { TableGenericComponent } from '@/shared/components/general/table-generic/table-generic.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { LayoutService } from '@/layout/service/layout.service';
import { MainBase } from '@/abstract/MainBase.abstract';
import { FormConfig } from '@/interface/IDynamicCrud';
import { TableService } from './service/table.service';
import { GeneralService } from './service/general.service';
import { FormBuilderService } from './service/form-builder.service';
import { IProgramaPermiso } from '@/interface/general/IProgramaPermiso.interface';
import { IResponseApiGeneric } from '@/interface/general/IResponseApi';
import { TypeResponse } from '@/common/enums/general/TypeResponse';
import { IConfiguracionSistema } from '../../../../pages/radicacion/flujo-doc-procesos/interfaces/flujo-doc-procesos.interface';
import { IFirmaDigitalForm } from '@/pages/configuraciones/flujo-doc/firma-digital/interface/dinamic-form.interface';
import { IActionEventCrud } from '@/common/enums/general.enum';
import { FooterModalService } from '@/shared/components/general/footer-modal/footer-modal.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  EFileType,
  FilePreviewComponent,
} from '@/shared/components/general/file-preview/file-preview.component';
import { FooterComponent } from '@/shared/components/footer';
import { ModalGenericComponent } from '@/shared/components/general/modal-generic/modal-generic.component';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-modal-documentos-relacionados',
  standalone: true,
  providers: [DialogService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    TableGenericComponent,
    ToastModule,
  ],
  templateUrl: './modal-documentos-relacionados.component.html',
  styleUrl: './modal-documentos-relacionados.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalDocumentosRelacionadosComponent extends MainBase implements OnDestroy, OnInit {
  override onSubmit(): void {
    throw new Error('Method not implemented.');
  }

  currentColumns = 2;
  showFormData = false;

  // Variables del Crud Dinamico
  formConfig!: FormConfig;
  isEditMode = false;
  idArchivoResaltado: number | null = null;

  public showModalNewRegister = false;

  // Data para la Tablet Generic
  tableService = inject(TableService);
  titleService = inject(LayoutService);
  layoutService = inject(LayoutService);
  generalService = inject(GeneralService);
  formBuilderService = inject(FormBuilderService);
  public footerService = inject(FooterModalService);

  formGroup!: FormGroup<IFirmaDigitalForm>;

  constructor(
    public dialogService: DialogService,
    private messageService: MessageService,
    public config: DynamicDialogConfig<IActionEventCrud<any>>
  ) {
    super();
    this.actionToDo();
    this.footerService.resetButton();
    toObservable(this.footerService.presstButton)
      .pipe(takeUntilDestroyed())
      .subscribe((pressAction) => {
        switch (pressAction.action) {
          case 'ver':
            this.onVerDocumentosConcatenados();
            break;
          case 'comparar':
            this.onComparar();
            break;
          default:
            console.warn('⚠️ Acción no reconocida:', pressAction);
        }
      });
  }

  ngOnDestroy(): void {
  }

  ngOnInit() {
    this.tableService.setHeaderTable();
  }

  /**
   * Action to do in this view
   */
  actionToDo() {
    const action = this.config.data?.action;
    const record = this.config.data?.record;
    this.tableService.IdfdArchivo = record.IdfdArchivo;
  }

  //#region FORM Responsive
  changeColumns(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.currentColumns = parseInt(target.value);
  }

  getGridClasses(): string {
    const baseClasses = 'grid gap-4';

    switch (this.currentColumns) {
      case 1:
        return `${baseClasses} grid-cols-1`;
      case 2:
        return `${baseClasses} grid-cols-1 md:grid-cols-2`;
      case 3:
        return `${baseClasses} grid-cols-1 md:grid-cols-2 lg:grid-cols-3`;
      case 4:
        return `${baseClasses} grid-cols-1 gap-1 md:grid-cols-2 md:gap-2 lg:grid-cols-3 lg:gap-4 xl:grid-cols-4 xl:gap-3`;
      default:
        return `${baseClasses} grid-cols-1 md:grid-cols-2`;
    }
  }

  resetForm(): void {
    this.formGroup.reset();
    this.showFormData = false;
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach((key) => {
      this.formGroup.get(key)?.markAsTouched();
    });
  }
  //#end-regigon

  //#region DYNAMIC CRUD

  getErrorMessage(fieldName: string, form: FormGroup): string {
    const control = form.get(fieldName);
    if (!control?.errors) return '';

    const errors = control.errors;

    if (errors['required'])
      return `${this.getFieldDisplayName(fieldName)} es obligatorio`;
    if (errors['minlength'])
      return `${this.getFieldDisplayName(fieldName)} debe tener al menos ${
        errors['minlength'].requiredLength
      } caracteres`;
    if (errors['maxlength'])
      return `${this.getFieldDisplayName(fieldName)} no puede tener más de ${
        errors['maxlength'].requiredLength
      } caracteres`;
    if (errors['min'])
      return `${this.getFieldDisplayName(fieldName)} debe ser mayor a ${
        errors['min'].min
      }`;

    return 'Campo inválido';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      id: 'ID',
      nombre: 'Nombre',
      Estado: 'Estado',
      observacion: 'Observación',
    };
    return displayNames[fieldName] || fieldName;
  }

  //#end-region

  //#region  Carga inicial (Cuando no sea con filter)
  loadInitialData(): void {
    this.generalService.getInitialParameters$().subscribe({
      next: (response: IResponseApiGeneric<IConfiguracionSistema>) => {
        console.table(response);
        if (
          response?.status === TypeResponse.OK &&
          response.data.ProgramasPermisosList
        ) {
          this.programasPermisos = response.data.ProgramasPermisosList[0];
          this.setProgramaPermisos(this.programasPermisos);
        } else {
          console.warn(
            'La respuesta no tiene status OK o sin Carga Inicial:',
            response
          );
          this.messageService.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'Sin Permisos en la carga inicial',
            life: 10000,
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar los datos iniciales',
          life: 3000,
        });
      },
      complete: () => {}
    });
  }

  override setProgramaPermisos(programasPermisos: IProgramaPermiso): void {
    // // Aqui asignamos los permisos a la Botonera
    console.table(programasPermisos);
    // this.permissonService.setActiveProgramAndButtons(
    //   programasPermisos.CodPrograma,
    //   ['Grabar', 'Editar', 'Ejecutar']
    // );

    // this.tableService.CodProgram = programasPermisos.CodPrograma;
    // this.permissonService.setPermissionsForProgram(programasPermisos);
  }

  //#endregion

  //#region vistas del modal comparar
  onVerDocumentosConcatenados() {
    const record = this.config.data?.record;
    this.generalService.getPDFsByPaquete(record.IdfdArchivo, true).subscribe({
      next: (response) => {
        if (!response?.data?.fileBase64) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Sin datos',
            detail: 'No se encontró información del documento.',
          });
          return;
        }

        this.dialogService.open(FilePreviewComponent, {
          header: `Vista previa - Documento ${record.IdfdArchivo}`,
          width: '90vw',
          modal: true,
          closable: true,
          dismissableMask: true,
          maximizable: true,
          contentStyle: {
            'max-height': '90vh',
            'border-radius': '8px',
            'box-shadow': '0 10px 30px rgba(51, 27, 27, 0.2)',
          },
          templates: {
            footer: FooterComponent,
          },
          data: {
            fileType: EFileType.PdfBase64,
            fileUrl: [response.data.fileBase64],
          },
        });
      },
      error: (err) => {
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo obtener el archivo PDF',
        });
      },
    });
  }

  onComparar() {
    const datos = this.tableService.currentDataTable;
    const lista = datos?.data ?? [];
    const record = this.config.data?.record;
    if (!record) {
      return;
    }
    const ref = this.dialogService.open(ModalGenericComponent, {
      header: 'Seleccione un Documento',
      width: '40vw',
      height: '30vh',
      modal: true,
      closable: true,
      dismissableMask: true,
      maximizable: true,
      data: {
        title: '¡Selección de un Registro!',
        message: 'Seleccione un documento para comparar:',
        tipoConfirmacion: 'success',
        confirmLabel: 'Comparar',
        cancelLabel: 'Cancelar',
        documentos: lista.map((d: any) => ({
          label: `${d.IdfdArchivo}`,
          value: d.IdfdArchivo,
        })),
      },
    });

    ref.onClose.subscribe((selectedId: number | null) => {
      if (!selectedId) {
        return;
      }

      const pdfArchivo$ = this.generalService.getPDFByArchivo(selectedId, true);
      const pdfPaquete$ = this.generalService.getPDFsByPaquete(
        record.IdfdArchivo,
        true
      );

      forkJoin([pdfArchivo$, pdfPaquete$]).subscribe({
        next: ([resArchivo, resPaquete]) => {
          // Aquí extraemos el Base64 de ambas respuestas
          const base64_1 = resArchivo?.data?.fileBase64 || null;
          const base64_2 = resPaquete?.data?.fileBase64 || null;

          if (!base64_1 || !base64_2) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Sin datos',
              detail: 'No se encontraron archivos PDF para comparar.',
            });
            return;
          }

          // Abrir vista previa doble
          this.dialogService.open(FilePreviewComponent, {
            header: `Comparación de documentos`,
            width: '90vw',
            height: '90vh',
            modal: true,
            closable: true,
            maximizable: true,
            dismissableMask: true,
            templates: {
              footer: FooterComponent,
            },
            data: {
              fileType: EFileType.PdfBase64,
              fileUrl: [base64_1, base64_2],
            },
          });
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron obtener los archivos para comparar.',
          });
        },
      });
    });
  }
  //#endregion
}
